<script lang="ts" setup>
defineOptions({ name: 'SolutionsPage' })

import { reactive, computed, ref, onMounted, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { Icon } from '@/components/Icon'
import {
  DEFAULT_SOLUTION_TABS,
  getPortalSolutionCatalog,
  getPortalSolutionTabs,
  type PortalSolutionCatalogItem,
  type PortalSolutionTabItem
} from '../data/portalCatalog'

const route = useRoute()
const router = useRouter()

const state = reactive({
  activeTab: '' as string,
  loading: false
})

type DisplaySolution = {
  id: string
  name: string
  icon: string
  color: string
  desc: string
  products: string[]
  coverImage: string
  path: string
  categoryKey: string
}

const solutionTabs = ref<PortalSolutionTabItem[]>([])
const allSolutions = ref<DisplaySolution[]>([])

function mapCatalogItem(item: PortalSolutionCatalogItem): DisplaySolution {
  return {
    id: item.id,
    name: item.name,
    icon: item.icon || 'lucide:layers',
    color: item.color || '#0066FF',
    desc: item.desc || item.effect,
    products: item.products,
    coverImage: item.coverImage || '',
    path: item.path || '',
    categoryKey: item.categoryKey
  }
}

function resolveInitialTab(tabs: PortalSolutionTabItem[]) {
  const fromQuery = String(route.query.tab || '').trim()
  if (fromQuery && tabs.some((t) => t.key === fromQuery)) return fromQuery
  return tabs[0]?.key || ''
}

const filteredSolutions = computed(() =>
  allSolutions.value.filter((s) => s.categoryKey === state.activeTab)
)

function setFilter(tabKey: string) {
  if (state.activeTab === tabKey) return
  state.activeTab = tabKey
  router.replace({
    path: route.path,
    query: { ...route.query, tab: tabKey }
  })
}

function openLink(path: string) {
  if (!path) return
  window.open(path, '_blank', 'noopener,noreferrer')
}

function goToDetail(item: DisplaySolution) {
  openLink(item.path || `/portal/solutions/${item.categoryKey}/${item.id}`)
}

onMounted(async () => {
  state.loading = true
  try {
    const [tabResult, catalogResult] = await Promise.allSettled([
      getPortalSolutionTabs(),
      getPortalSolutionCatalog()
    ])

    // 筛选项 = 后台已发布分类（tab/list），不写死四项
    if (tabResult.status === 'fulfilled' && tabResult.value.length) {
      solutionTabs.value = tabResult.value.filter((t) => t.enabled)
    } else {
      solutionTabs.value = [...DEFAULT_SOLUTION_TABS]
    }

    state.activeTab = resolveInitialTab(solutionTabs.value)

    // 卡片 = 后台方案入口（entry/list），按当前分类 tabKey 过滤
    if (catalogResult.status === 'fulfilled' && catalogResult.value.length) {
      allSolutions.value = catalogResult.value.map(mapCatalogItem)
    }
  } catch {
    solutionTabs.value = [...DEFAULT_SOLUTION_TABS]
    state.activeTab = resolveInitialTab(solutionTabs.value)
  } finally {
    state.loading = false
  }
})

watch(
  () => route.query.tab,
  (tab) => {
    const key = String(tab || '').trim()
    if (key && solutionTabs.value.some((t) => t.key === key) && key !== state.activeTab) {
      state.activeTab = key
    }
  }
)
</script>

<template>
  <div class="solutions-page">
    <!-- Hero Banner -->
    <div class="hero-banner">
      <div class="hero-content">
        <h1 class="hero-title">解决方案</h1>
        <p class="hero-subtitle">电商 · 跨境 · 行业 · 智慧建设，深度场景开箱即用</p>
      </div>
    </div>

    <!-- Content -->
    <div class="content-wrap">
      <!-- 四大方案分类 -->
      <div class="filter-bar" role="tablist" aria-label="方案分类">
        <button
          v-for="tab in solutionTabs"
          :key="tab.key"
          type="button"
          role="tab"
          class="filter-btn"
          :class="{ 'filter-active': state.activeTab === tab.key }"
          :aria-selected="state.activeTab === tab.key"
          @click="setFilter(tab.key)"
        >
          {{ tab.label }}
        </button>
      </div>

      <!-- Solution Grid -->
      <div v-loading="state.loading" class="solutions-grid">
        <div
          v-for="item in filteredSolutions"
          :key="`${item.categoryKey}-${item.id}`"
          class="solution-card card-hover"
          @click="goToDetail(item)"
        >
          <div
            class="card-top"
            :class="{ 'has-cover': !!item.coverImage }"
            :style="
              item.coverImage
                ? undefined
                : { background: `linear-gradient(135deg,${item.color}22,${item.color}44)` }
            "
          >
            <img
              v-if="item.coverImage"
              class="card-cover"
              :src="item.coverImage"
              :alt="item.name"
            />
            <Icon
              v-else
              icon="lucide:layers"
              :size="56"
              :style="{ color: item.color || '#0066FF', opacity: 0.6 }"
            />
          </div>
          <div class="card-bottom">
            <h3 class="card-name">{{ item.name }}</h3>
            <p class="card-desc">{{ item.desc }}</p>
            <div v-if="item.products.length" class="card-tags">
              <span v-for="pr in item.products" :key="pr" class="card-tag">{{ pr }}</span>
            </div>
            <div class="card-footer">
              <span class="card-link" :style="{ color: item.color || '#0066FF' }">
                了解详情
                <Icon icon="lucide:arrow-right" :size="14" />
              </span>
            </div>
          </div>
        </div>
      </div>

      <div
        v-if="!state.loading && !filteredSolutions.length"
        class="solutions-empty"
      >
        该分类下暂无方案，请稍后再看
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
// ===== Hero Banner =====
.hero-banner {
  height: 200px;
  background: linear-gradient(135deg, #0066ff, #0891b2);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
}

.hero-content {
  text-align: center;
}

.hero-title {
  font-size: 36px;
  font-weight: 800;
  margin-bottom: 8px;
}

.hero-subtitle {
  font-size: 16px;
  opacity: 0.8;
}

// ===== Content =====
.content-wrap {
  padding: 64px 24px;
  max-width: 1280px;
  margin: 0 auto;
}

// ===== Filter Bar =====
.filter-bar {
  display: flex;
  justify-content: center;
  gap: 8px;
  margin-bottom: 40px;
  flex-wrap: wrap;
}

.filter-btn {
  padding: 8px 20px;
  border-radius: 20px;
  font-size: 13px;
  font-weight: 500;
  border: 1px solid #e5e7eb;
  background: #fff;
  color: #4b5563;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: #0066ff;
    color: #0066ff;
  }
}

.filter-active {
  background: #0066ff;
  color: #fff;
  border-color: #0066ff;

  &:hover {
    background: #0044cc;
    color: #fff;
    border-color: #0044cc;
  }
}

// ===== Solutions Grid =====
.solutions-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;
  min-height: 120px;
}

.solutions-empty {
  text-align: center;
  padding: 48px 16px;
  color: #9ca3af;
  font-size: 14px;
}

.solution-card {
  border: 1px solid #e5e7eb;
  border-radius: 16px;
  overflow: hidden;
  cursor: pointer;
  background: #fff;
  transition: all 0.35s cubic-bezier(0.4, 0, 0.2, 1);

  &:hover {
    transform: translateY(-8px);
    box-shadow:
      0 24px 60px rgba(0, 102, 255, 0.12),
      0 4px 12px rgba(0, 0, 0, 0.04);
  }
}

.card-top {
  height: 140px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  background: #f3f4f6;

  &.has-cover {
    padding: 0;
  }
}

.card-cover {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.card-bottom {
  padding: 24px;
}

.card-name {
  font-size: 18px;
  font-weight: 700;
  margin-bottom: 8px;
  color: #111827;
}

.card-desc {
  font-size: 14px;
  color: #6b7280;
  line-height: 1.6;
  margin-bottom: 16px;
}

.card-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 16px;
}

.card-tag {
  font-size: 12px;
  padding: 3px 10px;
  border-radius: 4px;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  color: #6b7280;
}

.card-footer {
  display: flex;
  justify-content: flex-end;
  align-items: center;
}

.card-link {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 14px;
  font-weight: 600;
  transition: gap 0.3s ease;
}

.solution-card:hover .card-link {
  gap: 8px;
}

// ===== Responsive =====
@media (max-width: 1024px) {
  .solutions-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 768px) {
  .hero-banner {
    height: 160px;
  }

  .hero-title {
    font-size: 28px;
  }

  .hero-subtitle {
    font-size: 14px;
  }

  .solutions-grid {
    grid-template-columns: 1fr;
  }

  .filter-bar {
    gap: 6px;
  }

  .filter-btn {
    padding: 6px 14px;
    font-size: 12px;
  }
}
</style>
